import * as React from 'react'
import { CalendarIcon } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface MonthPickerProps {
  value: string | null
  onValueChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  monthsBack?: number
  monthsForward?: number
}

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

export function MonthPicker({
  value,
  onValueChange,
  placeholder = 'Seleccionar período',
  disabled = false,
  monthsBack = 12,
  monthsForward = 3,
}: MonthPickerProps) {
  // Generar opciones de períodos disponibles
  const periodOptions = React.useMemo(() => {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth()

    const options: { value: string; label: string; isCurrent: boolean }[] = []

    // Generar desde monthsBack atrás hasta monthsForward adelante
    for (let offset = -monthsBack; offset <= monthsForward; offset++) {
      const date = new Date(currentYear, currentMonth + offset, 1)
      const year = date.getFullYear()
      const month = date.getMonth()
      const mesNombre = MESES[month]
      const value = `${mesNombre} ${year}`
      const isCurrent = offset === 0

      options.push({
        value,
        label: isCurrent ? `${mesNombre} ${year} (actual)` : `${mesNombre} ${year}`,
        isCurrent,
      })
    }

    // Ordenar: más reciente primero
    return options.reverse()
  }, [monthsBack, monthsForward])

  return (
    <Select
      value={value || '__all__'}
      onValueChange={(val) => onValueChange(val === '__all__' ? null : val || null)}
      disabled={disabled}
    >
      <SelectTrigger>
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          <SelectValue placeholder={placeholder} />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__" className="text-muted-foreground">
          Todos los períodos
        </SelectItem>
        {periodOptions.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className={option.isCurrent ? 'font-medium' : ''}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
